import React, { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { getCategoryLabel } from "../../utils/categoryUtils";

const DocumentIcon = () => (
  <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const ChevronRight = () => (
  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const ChevronDown = ({ className = "" }) => (
  <svg className={`w-5 h-5 transition-transform ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const PendingPayments = ({ expenses, formatAmount, totalPending, monthId, monthYear, monthNumber, customCategories = [], defaultExpanded = false }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const count = expenses?.length ?? 0;

  const handleRowClick = () => {
    if (monthId) {
      navigate(`/month/${monthId}`);
    }
  };

  const getSubtitle = (exp) => {
    if (exp.dueDay != null && monthYear != null && monthNumber != null) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dueDate = new Date(monthYear, monthNumber - 1, exp.dueDay);
      dueDate.setHours(0, 0, 0, 0);
      const diffMs = dueDate - today;
      const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return { text: "Vence hoy", isDueDate: true };
      if (diffDays === 1) return { text: "Vence en 1 día", isDueDate: true };
      if (diffDays > 1) return { text: `Vence en ${diffDays} días`, isDueDate: true };
      if (diffDays === -1) return { text: "Venció ayer", isDueDate: true };
      return { text: `Venció hace ${Math.abs(diffDays)} días`, isDueDate: true };
    }
    const categoryLabel = getCategoryLabel(exp.category, customCategories);
    return { text: categoryLabel || "Sin categoría", isDueDate: false };
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded((prev) => !prev)}
        className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2">
          <DocumentIcon />
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Pendientes por pagar</h3>
        </div>
        <div className="flex items-center gap-3">
          {count > 0 && (
            <span className="px-3 py-1 rounded-full bg-amber-200 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200 text-sm font-semibold">
              {count} pendiente{count !== 1 ? "s" : ""}
            </span>
          )}
          <ChevronDown className={`text-gray-500 dark:text-gray-400 ${isExpanded ? "rotate-180" : ""}`} />
        </div>
      </button>

      {isExpanded && (
        <div className="px-6 pb-6 pt-0">
      {(!expenses || expenses.length === 0) ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No hay pendientes</p>
      ) : (
        <>
          <ul className="space-y-3">
            {expenses.map((exp) => (
              <li
                key={exp.id}
                className="flex items-center justify-between py-3 px-3 -mx-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                onClick={handleRowClick}
              >
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">{exp.name}</p>
                  {(() => {
                    const sub = getSubtitle(exp);
                    return (
                      <p className={`text-sm ${sub.isDueDate ? "text-orange-600 dark:text-orange-500" : "text-gray-500 dark:text-gray-400"}`}>
                        {sub.text}
                      </p>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-900 dark:text-white">
                    {formatAmount ? formatAmount(exp.amount) : exp.amount}
                  </span>
                  <ChevronRight />
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <span className="text-gray-600 dark:text-gray-400">Total pendiente:</span>
              <span className="text-xl font-bold text-amber-600 dark:text-amber-500">
                {formatAmount ? formatAmount(totalPending) : totalPending}
              </span>
            </div>
          </div>
        </>
      )}
        </div>
      )}
    </div>
  );
};

PendingPayments.propTypes = {
  expenses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
      category: PropTypes.string,
      dueDay: PropTypes.number
    })
  ),
  customCategories: PropTypes.array,
  formatAmount: PropTypes.func,
  totalPending: PropTypes.number,
  monthId: PropTypes.string,
  monthYear: PropTypes.number,
  monthNumber: PropTypes.number,
  defaultExpanded: PropTypes.bool
};

export { PendingPayments };
